import * as React from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { MaskedTextBox } from '@progress/kendo-react-inputs';
import { FloatingLabel, Error, Hint } from '@progress/kendo-react-labels';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * A masked textbox form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.PropsWithoutRef<MaskedTextBox>} TelerikMaskedTextBoxProps
 *
 * @param {BaseFieldRenderProps & TelerikMaskedTextBoxProps} fieldRenderProps
 * @returns {React.ReactElement} Masked textbox form field.
 */
const FormMaskedTextBox = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    name,
    valid,
    hint,
    optional,
    disabled,
    value,
    placeholder,
    readOnly,
    wrapperClassName,
    defaultValue,
    ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const labelId = label ? `${editorId}_label` : '';
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';

  return (
    <FieldWrapper className={wrapperClassName}>
      <FloatingLabel
        label={label}
        id={labelId}
        optional={optional}
        editorId={editorId}
        editorValid={valid}
        editorDisabled={disabled}
        editorValue={value}
        editorPlaceholder={placeholder}
        {...(readOnly ? { className: 'label-readonly' } : {})}
      >
        <MaskedTextBox
          value={value || defaultValue}
          placeholder={placeholder}
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={editorId}
          name={name}
          disabled={disabled}
          {...others}
        />
      </FloatingLabel>
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export default FormMaskedTextBox;
